import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";
import { Megaphone, LayoutList, CreditCard, Package, Mail, LogOut, Plus, Trash2, ShieldCheck } from "lucide-react";

const TABS = [
  { id: "announcements", label: "Announcements", icon: Megaphone },
  { id: "listings", label: "Listings", icon: LayoutList },
  { id: "payments", label: "Payments", icon: CreditCard },
  { id: "orders", label: "Orders", icon: Package },
  { id: "messages", label: "Messages", icon: Mail },
];

const input = "w-full bg-[#14141c] border border-amber-400/15 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none";
const btnGold = "rounded-full bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37] text-[#1a1a12] font-medium px-5 py-2.5 hover:opacity-90 transition-opacity flex items-center gap-2";
const card = "rounded-2xl border border-white/10 bg-[#101017] p-5";

const Admin = () => {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("announcements");
  const [data, setData] = useState({ announcements: [], listings: [], payments: [], orders: [], messages: [] });

  const [ann, setAnn] = useState({ title: "", body: "", tone: "info" });
  const [lst, setLst] = useState({ category: "destination", title: "", subtitle: "", image: "", price: "", trip_type: "", blurb: "" });
  const [pay, setPay] = useState({ kind: "crypto", label: "", details: "", note: "" });

  const loadAll = useCallback(async () => {
    try {
      const [a, l, p, o, m] = await Promise.all([
        adminApi.get("/announcements"),
        adminApi.get("/listings"),
        adminApi.get("/admin/payment-methods"),
        adminApi.get("/admin/orders"),
        adminApi.get("/admin/messages"),
      ]);
      setData({ announcements: a.data, listings: l.data, payments: p.data, orders: o.data, messages: m.data });
    } catch (e) {}
  }, []);

  useEffect(() => {
    adminApi.get("/admin/me").then(() => { setAuthed(true); loadAll(); }).catch(() => { setAuthed(false); });
  }, [loadAll]);

  const logout = () => { localStorage.removeItem("mova_admin_token"); navigate("/"); };

  const addAnnouncement = async () => {
    if (!ann.title || !ann.body) return toast.error("Title and body required");
    await adminApi.post("/admin/announcements", ann);
    setAnn({ title: "", body: "", tone: "info" }); toast.success("Announcement posted"); loadAll();
  };
  const addListing = async () => {
    if (!lst.title) return toast.error("Title required");
    await adminApi.post("/admin/listings", lst);
    setLst({ category: "destination", title: "", subtitle: "", image: "", price: "", trip_type: "", blurb: "" });
    toast.success("Listing added"); loadAll();
  };
  const addPayment = async () => {
    if (!pay.label || !pay.details) return toast.error("Label and details required");
    await adminApi.post("/admin/payment-methods", { ...pay, active: true });
    setPay({ kind: "crypto", label: "", details: "", note: "" }); toast.success("Payment method added"); loadAll();
  };
  const del = async (url) => { await adminApi.delete(url); loadAll(); };
  const setStatus = async (id, status) => { await adminApi.put(`/admin/orders/${id}/status?status=${status}`); toast.success("Status updated"); loadAll(); };

  if (authed === null) return <div className="min-h-screen bg-obsidian flex items-center justify-center text-champagne">Loading…</div>;
  if (authed === false) return (
    <div className="min-h-screen bg-obsidian flex flex-col items-center justify-center text-center px-6">
      <ShieldCheck className="text-champagne mb-4" size={32} />
      <p className="font-serif text-2xl text-champagne">Admin access required</p>
      <p className="text-slate-400 mt-2">Tap the MOVA logo on the site to sign in.</p>
      <button onClick={() => navigate("/")} className={`${btnGold} mt-6`}>Back to site</button>
    </div>
  );

  return (
    <div className="min-h-screen bg-obsidian text-white">
      <header className="border-b border-amber-400/15 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-accent text-xl text-champagne">MOVA</span>
            <span className="text-xs tracking-[0.25em] text-amber-400/70 uppercase">Admin Center</span>
          </div>
          <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 text-sm text-slate-300 hover:text-white"><LogOut size={16} /> Sign out</button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`} className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm border transition-colors ${tab === t.id ? "bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37] text-[#1a1a12] border-transparent font-medium" : "border-amber-400/25 text-slate-300 hover:border-amber-400/50"}`}>
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "announcements" && (
          <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-announcements">
            <div className={card}>
              <h3 className="font-serif text-xl text-champagne mb-4">Post announcement</h3>
              <div className="space-y-3">
                <input className={input} placeholder="Title" value={ann.title} onChange={(e) => setAnn({ ...ann, title: e.target.value })} data-testid="ann-title" />
                <textarea className={`${input} resize-none`} rows={3} placeholder="Message" value={ann.body} onChange={(e) => setAnn({ ...ann, body: e.target.value })} data-testid="ann-body" />
                <select className={input} value={ann.tone} onChange={(e) => setAnn({ ...ann, tone: e.target.value })} data-testid="ann-tone">
                  <option value="info">Info</option><option value="sale">Sale</option><option value="new">New</option>
                </select>
                <button className={btnGold} onClick={addAnnouncement} data-testid="ann-add-btn"><Plus size={16} /> Post</button>
              </div>
            </div>
            <div className="space-y-3">
              {data.announcements.map((a) => (
                <div key={a.id} className={`${card} flex justify-between items-start`}>
                  <div><p className="text-champagne font-medium">{a.title}</p><p className="text-sm text-slate-400 mt-1">{a.body}</p></div>
                  <button onClick={() => del(`/admin/announcements/${a.id}`)} className="text-slate-500 hover:text-red-400"><Trash2 size={16} /></button>
                </div>
              ))}
              {data.announcements.length === 0 && <p className="text-slate-500 text-sm">No announcements yet.</p>}
            </div>
          </div>
        )}

        {tab === "listings" && (
          <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-listings">
            <div className={card}>
              <h3 className="font-serif text-xl text-champagne mb-4">Add listing</h3>
              <div className="space-y-3">
                <select className={input} value={lst.category} onChange={(e) => setLst({ ...lst, category: e.target.value })} data-testid="lst-category">
                  <option value="destination">Destination</option><option value="trip">Trip</option><option value="fashion">Fashion</option><option value="service">Self-Care service</option>
                </select>
                <input className={input} placeholder="Title" value={lst.title} onChange={(e) => setLst({ ...lst, title: e.target.value })} data-testid="lst-title" />
                <input className={input} placeholder="Subtitle / category" value={lst.subtitle} onChange={(e) => setLst({ ...lst, subtitle: e.target.value })} data-testid="lst-subtitle" />
                <input className={input} placeholder="Image URL" value={lst.image} onChange={(e) => setLst({ ...lst, image: e.target.value })} data-testid="lst-image" />
                <input className={input} placeholder="Price / deal" value={lst.price} onChange={(e) => setLst({ ...lst, price: e.target.value })} data-testid="lst-price" />
                {lst.category === "trip" && <input className={input} placeholder="Trip type (Flights/Hotels/Experiences/Group Trips)" value={lst.trip_type} onChange={(e) => setLst({ ...lst, trip_type: e.target.value })} data-testid="lst-triptype" />}
                <input className={input} placeholder="Blurb / duration" value={lst.blurb} onChange={(e) => setLst({ ...lst, blurb: e.target.value })} data-testid="lst-blurb" />
                <button className={btnGold} onClick={addListing} data-testid="lst-add-btn"><Plus size={16} /> Add</button>
              </div>
            </div>
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              {data.listings.map((l) => (
                <div key={l.id} className={`${card} flex items-center gap-3`}>
                  {l.image && <img src={l.image} alt="" className="w-12 h-12 rounded-lg object-cover" />}
                  <div className="flex-1"><p className="text-white text-sm">{l.title} <span className="text-xs text-amber-400/70">· {l.category}</span></p><p className="text-xs text-slate-500">{l.subtitle} {l.price}</p></div>
                  <button onClick={() => del(`/admin/listings/${l.id}`)} className="text-slate-500 hover:text-red-400"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "payments" && (
          <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-payments">
            <div className={card}>
              <h3 className="font-serif text-xl text-champagne mb-4">Add payment method</h3>
              <div className="space-y-3">
                <select className={input} value={pay.kind} onChange={(e) => setPay({ ...pay, kind: e.target.value })} data-testid="pay-kind">
                  <option value="crypto">Crypto wallet</option><option value="bank">Bank transfer</option>
                </select>
                <input className={input} placeholder={pay.kind === "crypto" ? "Label e.g. USDT (TRC20)" : "Label e.g. Chase Bank"} value={pay.label} onChange={(e) => setPay({ ...pay, label: e.target.value })} data-testid="pay-label" />
                <textarea className={`${input} resize-none`} rows={3} placeholder={pay.kind === "crypto" ? "Wallet address & network" : "Account name, number, routing/SWIFT"} value={pay.details} onChange={(e) => setPay({ ...pay, details: e.target.value })} data-testid="pay-details" />
                <input className={input} placeholder="Note (optional)" value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} data-testid="pay-note" />
                <button className={btnGold} onClick={addPayment} data-testid="pay-add-btn"><Plus size={16} /> Add method</button>
                <p className="text-xs text-slate-500">Card checkout via Stripe can be enabled later.</p>
              </div>
            </div>
            <div className="space-y-3">
              {data.payments.map((p) => (
                <div key={p.id} className={`${card} flex justify-between items-start`}>
                  <div><p className="text-champagne font-medium">{p.label} <span className="text-xs text-slate-500 uppercase">{p.kind}</span></p><p className="text-sm text-slate-400 mt-1 break-words">{p.details}</p></div>
                  <button onClick={() => del(`/admin/payment-methods/${p.id}`)} className="text-slate-500 hover:text-red-400"><Trash2 size={16} /></button>
                </div>
              ))}
              {data.payments.length === 0 && <p className="text-slate-500 text-sm">No payment methods yet.</p>}
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div className="space-y-3" data-testid="admin-orders">
            {data.orders.map((o) => (
              <div key={o.id} className={`${card} flex flex-col sm:flex-row sm:items-center gap-3`}>
                <div className="flex-1">
                  <p className="text-white text-sm">{o.title || "—"} <span className="text-xs text-amber-400/70 uppercase">· {o.brand}</span></p>
                  <a href={o.product_url} target="_blank" rel="noreferrer" className="text-xs text-sky-400 break-all">{o.product_url}</a>
                  <p className="text-xs text-slate-500 mt-1">{o.user_name} ({o.user_email}) · Qty {o.qty} {o.size && `· ${o.size}`}</p>
                </div>
                <select value={o.status} onChange={(e) => setStatus(o.id, e.target.value)} data-testid={`order-status-${o.id}`} className="bg-[#14141c] border border-amber-400/15 rounded-lg px-3 py-2 text-sm text-white">
                  <option value="pending">Pending</option><option value="paid">Paid</option><option value="fulfilled">Fulfilled</option><option value="cancelled">Cancelled</option>
                </select>
              </div>
            ))}
            {data.orders.length === 0 && <p className="text-slate-500 text-sm">No orders yet.</p>}
          </div>
        )}

        {tab === "messages" && (
          <div className="space-y-3" data-testid="admin-messages">
            {data.messages.map((m) => (
              <div key={m.id} className={card}><p className="text-champagne font-medium">{m.name} <span className="text-xs text-slate-500">{m.email}</span></p><p className="text-sm text-slate-300 mt-1">{m.message}</p></div>
            ))}
            {data.messages.length === 0 && <p className="text-slate-500 text-sm">No messages yet.</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
